// The slot list (ADR 0011). A comparison shows one unit per column, and a column is a unit
// under its OWN stat frame, so every frame key — `?tech=`, `?corruption=`, `?items=`,
// `?level=` — carries one slot per column on /compare. The separator is `;` — a sub-delim
// that survives `URLSearchParams` and `URL` unsplit (verified 2026-09-06), and which a
// serializer may percent-encode as `%3B`, correct either way. Within a slot each axis's own
// grammar is unchanged, so a value with no `;` in it is exactly a single-hull value, byte
// for byte. An empty slot is the axis's default (Base, uncorrupted, no items, level 1).
//
// One module for all four keys: the grammar is the comparison's, not any one axis's, and
// it lived in tech-set.ts under alias exports on two other modules until review 2026-09-08.

export const SLOT_SEPARATOR = ";";

/** A slotted value as its per-column slots. An absent or empty value names no column at all
 * (not one empty slot), so a bare link stays a bare link; every other value yields exactly
 * one slot more than it holds separators — a trailing `;` therefore names a final default
 * column, and a leading one a first default column. */
export function splitSlots(value: string | undefined): string[] {
  if (value === undefined || value === "") return [];
  return value.split(SLOT_SEPARATOR);
}

/** The slots back into one value. Trailing default slots are trimmed — they say nothing a
 * missing slot does not already say — so a matchup whose later columns are Base writes the
 * same URL a shorter matchup would, and an all-default list stays out of the URL entirely.
 * Leading and interior empties are kept: they are what places the slots after them. */
export function joinSlots(slots: readonly (string | undefined)[]): string | undefined {
  const values = slots.map((slot) => slot ?? "");
  while (values.length > 0 && values[values.length - 1] === "") values.pop();
  return values.length === 0 ? undefined : values.join(SLOT_SEPARATOR);
}

/** A search value as the router can hand it over. The router parses search values with
 * JSON.parse and stringifies them back the same way, so a value that spells a bare whole
 * number (`?level=10`, `?points=2`) arrives as a NUMBER and must leave as one — a string "10"
 * would reach the URL as `?level=%2210%22` (reproduced on the served build 2026-09-07 and
 * again 2026-09-10). Every other spelling, the `;`-slotted ones included, stays a string.
 * Both mean the same thing; the two numeric keys read the value as text either way. */
export type UrlValue = string | number;

/** The token a value spells, or undefined for an absent one. */
export const asToken = (value: UrlValue | undefined): string | undefined =>
  value === undefined ? undefined : String(value);

/** A joined value in the spelling the router must be handed: a lone all-digit value as a
 * NUMBER, anything else as it is. Decided once here for both numeric keys rather than beside
 * each serializer (review 2026-09-10: three copies of the same regex). */
export function routerValue(joined: string | undefined): UrlValue | undefined {
  return joined !== undefined && /^\d+$/.test(joined) ? Number(joined) : joined;
}
